import { CustomField, getFormattedAddress } from '@sales-center/common'
import {
  IBillToLocation,
  IServiceLocation,
} from '@user/store/locations/typings'

import { createInitialValues, ILeadFormModel } from '../typings'
import { formatInitialPhoneNumberValue } from '.'
import { NoteCode } from '@user/components/Notes/typings'

export function getInitialValuesForLocation(
  customFields: CustomField[],
  location: IServiceLocation | undefined,
  phone?: string | null,
  noteCodes?: NoteCode[],
): ILeadFormModel {
  const initialValues = createInitialValues(customFields, [], phone)

  if (location) {
    const address = location?.address
    const emailsSplit = location.email.includes(',')
      ? location?.email.split(',')
      : location?.email.split(';')

    const usableEmails = emailsSplit.splice(0, 3)
    const extraEmails = emailsSplit.splice(4).join(', ')

    const billTO = location.billTo || null
    const billToEmails = billTO.email?.includes(',')
      ? billTO?.email?.split(',')
      : billTO?.email?.split(';')

    const usableBillToEmails = billToEmails?.splice(0, 3)

    const { phoneNumber, altPhoneNumber, phoneExt, altPhoneExt } =
      getPhoneNumberValues(location)
    const {
      phoneNumber: billTo_phoneNumber,
      altPhoneNumber: billTo_altPhoneNumber,
      phoneExt: billTo_phoneExt,
      altPhoneExt: billTo_altPhoneExt,
    } = getPhoneNumberValues(billTO)

    return {
      ...initialValues,
      externalLocationId: location.id || '',
      externalLocationCode: location.number || '',

      billingLocation: false,
      locationSameAsBillTo: !location.id,
      firstName: location.firstName || '',
      lastName: location.lastName || '',
      company: location.company || '',
      phoneNumber: phoneNumber || (phone ?? ''),
      phoneExt: phoneExt,
      emailAddresses: usableEmails
        ? usableEmails.map((e, index) => ({
            emailAddress: e,
            isPrimary: index === 0,
          }))
        : [{ emailAddress: '', isPrimary: true }],
      address1: address.address || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      postalCode: address.postalCode || '',
      altPhoneExt: altPhoneExt,
      altPhoneNumber: altPhoneNumber,
      title: '',
      mobilePhone: location.mobile || '',
      county: '',
      latitude: address.lat || 0,
      longitude: address.lng || 0,
      externalLinkId: '',
      externalLinkType: '',
      contactTime: '',
      contactMethod: '',

      division: location.division || '',
      divisionId: location.divisionId || '',
      locationType: location.locationType || '',
      locationTypeId: location.locationTypeId || '',
      primaryServiceLocation: false,

      billTo_primaryServiceLocation: false,
      billTo_billingLocation: true,

      billTo_firstName: billTO?.firstName || '',
      billTo_lastName: billTO?.lastName || '',
      billTo_title: billTO?.title || '',
      billTo_company: billTO?.company || '',
      billTo_emailAddresses: usableBillToEmails
        ? usableBillToEmails.map((e, index) => ({
            emailAddress: e,
            isPrimary: index === 0,
          }))
        : [{ emailAddress: '', isPrimary: true }],
      billTo_phoneNumber: billTo_phoneNumber ?? '',
      billTo_phoneExt: billTo_phoneExt,
      billTo_mobilePhone: billTO?.mobile || '',
      billTo_altPhoneNumber: billTo_altPhoneNumber,
      billTo_altPhoneExt: billTo_altPhoneExt,
      billTo_address1: billTO?.address?.address || '',
      billTo_address2: billTO?.address?.address2 || '',
      billTo_city: billTO?.address?.city || '',
      billTo_state: billTO?.address?.state || '',
      billTo_postalCode: billTO?.address?.postalCode || '',
      billTo_county: billTO?.address?.country || '',
      billTo_latitude: billTO?.address?.lat || 0,
      billTo_longitude: billTO?.address?.lng || 0,
      billTo_externalLinkId: '',
      billTo_externalLinkType: '',
      billTo_contactTime: '',
      billTo_contactMethod: '',

      billTo_divisionId: '',
      billTo_division: '',
      billTo_locationTypeId: '',
      billTo_locationType: '',

      billTo_externalLocationId: location.billTo?.id || '',
      billTo_externalLocationCode: location.billTo?.number || '',

      country: address.country || '',
      lat: address.lat || 0,
      lng: address.lng || 0,
      campaignId: location.campaignId || '',
      campaign: location.campaign || '',
      leadSourceId: location.sourceId || '',
      leadSource: location.source || '',
      branchId: location.branchId || '',
      branch: location.branch || '',
      note: extraEmails,
      addNoteToPestPac: Boolean(location.id),
      serviceLocation: `${location.number} ${getFormattedAddress(
        address.address || '',
        address.address2 || '',
        address.city || '',
        address.state || '',
        address.postalCode || '',
      )}`,
      noteCode: noteCodes?.find((code) => code.isDefault)?.name || '',
      fullAddress: getFormattedAddress(
        address.address || '',
        address.address2 || '',
        address.city || '',
        address.state || '',
        address.postalCode || '',
      ),
    }
  } else {
    return initialValues
  }
}

function getPhoneNumberValues(location: IServiceLocation | IBillToLocation) {
  let phoneNumber = ''
  let altPhoneNumber = ''

  let phoneExt = ''
  let altPhoneExt = ''

  // WWM-18099 — a PestPac location can hold the same number in more than one
  // field (Sales Center itself copies Phone into Mobile when the number
  // validates as a mobile), so only promote a secondary number into Alt-Phone
  // when it differs from the one already shown as Phone. Both sides are
  // compared after formatting so "6091112222" and "1-609-111-2222" match.
  const setAltPhone = (number?: string | null, ext?: string | null) => {
    const formatted = formatInitialPhoneNumberValue(number) ?? ''
    if (!formatted || formatted === phoneNumber) return false

    altPhoneNumber = formatted
    altPhoneExt = ext || ''
    return true
  }

  if (location.phone) {
    phoneNumber = formatInitialPhoneNumberValue(location.phone) ?? ''
    phoneExt = location.phoneExt || ''

    if (!setAltPhone(location.altPhone, location.altPhoneExt)) {
      setAltPhone(location.mobile, location.mobileExt)
    }
  } else if (location.mobile) {
    phoneNumber = formatInitialPhoneNumberValue(location.mobile) ?? ''
    phoneExt = location.mobileExt || ''

    setAltPhone(location.altPhone, location.altPhoneExt)
  } else if (location.altPhone) {
    phoneNumber = formatInitialPhoneNumberValue(location.altPhone) ?? ''
    phoneExt = location.altPhoneExt || ''
  }

  return {
    phoneNumber,
    altPhoneNumber,
    phoneExt,
    altPhoneExt,
  }
}
